import {isValidURL, setRequireRegistrationUIDS} from '@lib/utils';

import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import ProductGroupDisplayComponent from '@components/ProductDisplayComponent';
import {ProductDisplayContextProvider} from '@contexts/providers';
import axios from '@lib/axios';
import {ProductFamilyDimensions} from '@components/ProductDisplayComponent/ProductDisplayVariance/ProductFamilyDimensions';
import {useTranslation} from 'next-i18next';
import {useEffect} from 'react';
import {useRouter} from 'next/router';
import FullPageLoader from '../../../components/FullPageLoader';

import {ProductDisplaysEnum} from '@enums/product-display';
import {ProductGroupDisplayResponseData} from '@interfaces/product-group-display';
import {GetStaticPropsContext} from 'next';
import {APIRequestConfig} from '@enums/request-config';
import {LocaleEnum} from '@enums/locale';
import {AxiosResponse} from 'axios';

export default function ProductFamilyDisplay({
  data,
}: {
  data: ProductGroupDisplayResponseData;
}) {
  const {t} = useTranslation('product-display');
  const router = useRouter();
  if (router.isFallback) {
    return <FullPageLoader />;
  }
  useEffect(() => {
    const uuid = router.query.uuid;
    if (uuid) {
      setRequireRegistrationUIDS(uuid as string);
    }
  }, [router.query.uuid]);
  return (
    <>
      <ProductDisplayContextProvider>
        <ProductGroupDisplayComponent
          data={data}
          ProductVarianceComponent={(() => {
            switch (data.type) {
              case ProductDisplaysEnum.PRODUCT_GROUP_DISPLAY_LIUNE_FAMILY:
              case ProductDisplaysEnum.PRODUCT_GROUP_DISPLAY_LIUNE_ART_DOOR:
                return (
                  <ProductFamilyDimensions
                    productGroupId={data.id}
                    productGroupUID={data.uid}
                    type={data.type}
                    lengthLabel={t('height')}
                    heightLabel={t('frame')}
                    widthLabel={t('width')}
                  />
                );
              case ProductDisplaysEnum.PRODUCT_GROUP_DISPLAY_LIUNE_ELEMENT:
                return (
                  <ProductFamilyDimensions
                    productGroupId={data.id}
                    productGroupUID={data.uid}
                    type={data.type}
                    lengthLabel={t('height')}
                    heightLabel={t('frame')}
                    widthLabel={t('access_opening')}
                  />
                );
              case ProductDisplaysEnum.PRODUCT_GROUP_DISPLAY_LIUNE_SURFACE_DOOR:
                return (
                  <ProductFamilyDimensions
                    type={data.type}
                    productGroupId={data.id}
                    productGroupUID={data.uid}
                    lengthLabel={t('height')}
                    heightLabel={t('thickness')}
                    widthLabel={t('width')}
                  />
                );
              case ProductDisplaysEnum.PRODUCT_GROUP_DISPLAY_LIUNE_SPECIAL_DOOR_FRAME:
                return (
                  <ProductFamilyDimensions
                    type={data.type}
                    productGroupId={data.id}
                    productGroupUID={data.uid}
                    lengthLabel={t('height')}
                    heightLabel={t('frame_depth')}
                    widthLabel={t('mounting_hole_width')}
                  />
                );
              case ProductDisplaysEnum.PRODUCT_GROUP_DISPLAY_LIUNE_DOOR_FRAME:
                return (
                  <ProductFamilyDimensions
                    type={data.type}
                    productGroupId={data.id}
                    productGroupUID={data.uid}
                    lengthLabel={t('height')}
                    heightLabel={t('door')}
                    widthLabel={t('width')}
                  />
                );
            }
          })()}
        />
      </ProductDisplayContextProvider>
    </>
  );
}

export async function getStaticProps({params, locale}: GetStaticPropsContext) {
  try {
    const slug = params?.slug;
    const response: AxiosResponse<{data: ProductGroupDisplayResponseData}> =
      await axios.get<any>(`store/productgroup/${slug}`, {
        headers: {
          [APIRequestConfig.X_LOCALE]: locale!,
        },
      });

    const data = response.data.data;
    const redirectUrl =
      data[`product_group_redirect_url_${locale as LocaleEnum}`];
    if (redirectUrl && isValidURL(redirectUrl)) {
      return {
        redirect: {
          destination: redirectUrl,
          permanent: false,
        },
      };
    }
    return {
      props: {
        ...(await serverSideTranslations(locale!, [
          'common',
          'product-display',
        ])),
        data: response.data.data,
      },
    };
  } catch (e: any) {
    if (e.response.status < 500) {
      return {notFound: true};
    }
    throw e;
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}
